import React, { forwardRef, type ReactNode } from 'react'
import { ThemeProvider, makeStyles } from '@mui/styles'
import { StyledEngineProvider } from '@mui/material'
import theme from '@/theme'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ToastProgress from './toast-progress'
import useToastController, { type SubscribeToToastStack, type ToastKey } from './use-toast-controller'

import CloseIcon from '@assets/images/icons/close.svg'

type ToastFrameVariant = 'standard' | 'banking'

interface ToastFrameStyleProps {
  accentColor: string
  variant: ToastFrameVariant
  stackPosition: number
  isExiting: boolean
}

interface ToastFrameProps {
  snackBarKey: ToastKey
  type: string
  accentColor: string
  autoHideDuration?: number | null
  subscribeToToastStack?: SubscribeToToastStack
  removeToast?: (key: ToastKey) => void
  variant?: ToastFrameVariant
  children: ReactNode
}

const useStyles = makeStyles(() => ({
  toast: ({ accentColor, variant, stackPosition, isExiting }: ToastFrameStyleProps) => {
    const isForeground = stackPosition === 0
    const isBackground = stackPosition > 0 && !isExiting
    const showExitIndicator = isExiting && stackPosition > 0

    return {
      zIndex: isExiting ? 3 : (isForeground ? 2 : 1),
      ...(variant === 'banking'
        ? {
          padding: 20,
          paddingRight: 40,
          minWidth: 425,
          minHeight: 110,
          flexDirection: 'column',
          alignItems: 'start',
        }
        : {
          width: 'fit-content',
          minWidth: 360,
          minHeight: 70,
          alignItems: 'center',
        }),
      maxWidth: 500,
      display: 'flex',
      position: 'relative',
      overflow: showExitIndicator ? 'visible' : 'hidden',
      opacity: isBackground ? 0.55 : 1,
      visibility: 'visible',
      pointerEvents: isForeground && !isExiting ? 'auto' : 'none',
      backgroundColor: isForeground || isBackground ? theme.white : 'transparent',
      borderLeft: isForeground || isBackground
        ? `4px solid ${accentColor}`
        : 'none',
      boxShadow: isForeground
        ? '0 3px 6px 0 rgba(0, 0, 0, 0.17)'
        : (isBackground
          ? '0 2px 5px 0 rgba(0, 0, 0, 0.12)'
          : 'none'),
      borderRadius: `0 ${theme.borderRadius} ${theme.borderRadius} 0`,
      transition: [
        'opacity 450ms ease',
        'background-color 450ms ease',
        'box-shadow 450ms ease',
        'border-left-color 450ms ease',
      ].join(', '),
      willChange: 'opacity',
      '& > *': {
        opacity: isForeground || isBackground ? 1 : 0,
        filter: isBackground ? 'blur(2px)' : 'blur(0)',
        transition: [
          'opacity 450ms ease',
          'filter 450ms ease',
        ].join(', '),
        willChange: 'opacity, filter',
      },
      '&::after': {
        content: showExitIndicator ? '""' : 'none',
        position: 'absolute',
        bottom: -10,
        left: '50%',
        width: 120,
        height: 6,
        transform: 'translateX(-50%)',
        backgroundColor: theme.white,
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.17)',
        borderRadius: `0 0 ${theme.borderRadius} ${theme.borderRadius}`,
        pointerEvents: 'none',
      },
    }
  },
  close: ({ variant }: ToastFrameStyleProps) => {
    return {
      position: 'absolute',
      top: variant === 'banking' ? 15 : 10,
      right: 15,
      border: 'none',
      outline: 'none',
      background: 'none',
      lineHeight: 1,
    }
  },
}))

const ToastFrame = forwardRef<HTMLElement, ToastFrameProps>(({ snackBarKey, type, accentColor, autoHideDuration, subscribeToToastStack, removeToast, variant = 'standard', children }, ref) => {
  const { stackPosition, isExiting, isProgressPaused, closeToast, onMouseEnter, onMouseLeave } = useToastController({ snackBarKey, type, subscribeToToastStack, removeToast })
  const classes = useStyles({ accentColor, variant, stackPosition, isExiting })

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <article
          className={classes.toast}
          ref={ref}
          key={snackBarKey}
          data-toast-type={type}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {children}
          <button
            type='button'
            className={classes.close}
            onClick={closeToast}
          >
            <SVGIcon
              svg={CloseIcon}
              height='10px'
              width='10px'
              fill={theme.lightGrey}
            />
          </button>
          <ToastProgress
            duration={autoHideDuration}
            color={accentColor}
            paused={isProgressPaused}
            onComplete={autoHideDuration != null ? closeToast : undefined}
          />
        </article>
      </ThemeProvider>
    </StyledEngineProvider>
  )
})

export default ToastFrame